import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageFlipViewer from '../components/PageFlipViewer';
import { FaCalendarAlt, FaUser, FaTag, FaShareAlt, FaFacebookF, FaTwitter, FaLinkedinIn, FaFilePdf, FaArrowLeft } from 'react-icons/fa';
import { format } from 'date-fns';
import { ChevronDown } from 'lucide-react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { useAdvertisementServices } from '../apis/advertisementService';
import GoToTop from '../components/GotToTop';
import { updateMetaTags, resetMetaTags, stripHtmlTags, extractFirstImageFromContent, getFullUrl } from '../utils/metaTags';
import PublicProtectedHtml, { PublicProtectedBlock } from '../components/PublicProtectedHtml';

const AdvertisementDetails = () => {
  const { id } = useParams();
  const [advertisement, setAdvertisement] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [readingProgress, setReadingProgress] = useState(0);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const { getAdvertisementById } = useAdvertisementServices();

  useEffect(() => {
    // Initialize AOS animation library safely
    try {
      if (typeof window !== 'undefined' && typeof AOS !== 'undefined') {
        if (!AOS.initialized) {
          AOS.init({
            duration: 800,
            once: false,
            easing: 'ease-in-out',
            offset: 120,
            delay: 50,
            mirror: true
          });
        } else {
          AOS.refresh();
        }
      }
    } catch (error) {
      console.warn('AOS initialization error:', error);
    }

    // Reading progress functionality
    const updateReadingProgress = () => {
      const currentProgress = window.scrollY;
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollHeight) {
        setReadingProgress(Number((currentProgress / scrollHeight).toFixed(2)) * 100);
      }
    };
    
    window.addEventListener('scroll', updateReadingProgress);

    // Fetch advertisement details
    fetchAdvertisementDetails();

    return () => {
      window.removeEventListener('scroll', updateReadingProgress);
      resetMetaTags();
    };
  }, [id]);

  const fetchAdvertisementDetails = async () => {
    setIsLoading(true);
    try {
      const response = await getAdvertisementById(id);
      
      if (response && response.success) {
        const adData = response.data;
        setAdvertisement(adData);
        
        // Update meta tags for social media sharing
        const description = stripHtmlTags(adData.content);
        const previewDescription = description.length > 160 ? description.substring(0, 160) + '...' : description;
        const image = adData.imageUrl || extractFirstImageFromContent(adData.content) || 'https://nchs-fe.vercel.app/images/alenalki.Logo.png';
        const url = getFullUrl(`/advertisement/${id}`);
        
        updateMetaTags({
          title: adData.title,
          description: previewDescription,
          image: image,
          url: url,
          type: 'article',
          author: adData.authorName || 'Alenalki'
        });
      } else {
        setError('Failed to fetch advertisement details');
      }
    } catch (error) {
      console.error('Error fetching advertisement details:', error);
      setError('An error occurred while fetching advertisement details');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-50">
        <div className="animate-pulse flex flex-col items-center">
          <div className="w-32 h-32 bg-blue-200 rounded-full mb-4"></div>
          <div className="h-4 bg-blue-200 rounded w-48 mb-2"></div>
          <div className="h-3 bg-blue-100 rounded w-40"></div>
        </div>
      </div>
    );
  }

  if (error || !advertisement) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-16">
          <div className="bg-white p-8 rounded-lg shadow-md text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
            <p className="text-gray-700 mb-6">{error || 'Advertisement not found'}</p>
            <Link to="/advertisements" className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
              Back to Advertisements
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <Header />
      
      {/* Reading Progress Bar */}
      <div className="fixed top-0 left-0 w-full h-1 z-50">
        <div 
          className="h-full bg-yellow-500 transition-all duration-300 ease-out"
          style={{ width: `${readingProgress}%` }}
        ></div>
      </div>
      
      {/* Advertisement Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <Link 
            to="/advertisements" 
            className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-8 group"
            data-aos="fade-right"
          >
            <FaArrowLeft className="mr-2 group-hover:-translate-x-1 transition-transform" />
            Back to All Advertisements
          </Link>
          
          {/* Social Share Sidebar */}
          <div className="hidden lg:block fixed left-8 top-1/2 transform -translate-y-1/2 space-y-4">
            <div className="flex flex-col items-center space-y-4">
              <span className="text-gray-500 text-sm font-medium">Share</span>
              <button 
                onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank', 'width=600,height=400')}
                className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 transition-colors"
                title="Share on Facebook"
              >
                <FaFacebookF />
              </button>
              <button 
                onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(advertisement?.title || '')}`, '_blank', 'width=600,height=400')}
                className="w-10 h-10 rounded-full bg-blue-400 text-white flex items-center justify-center hover:bg-blue-500 transition-colors"
                title="Share on Twitter/X"
              >
                <FaTwitter />
              </button>
              <button 
                onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`, '_blank', 'width=600,height=400')}
                className="w-10 h-10 rounded-full bg-blue-700 text-white flex items-center justify-center hover:bg-blue-800 transition-colors"
                title="Share on LinkedIn"
              >
                <FaLinkedinIn />
              </button>
              <div className="h-20 w-px bg-gray-300 my-2"></div>
              <span className="text-gray-500 text-sm font-medium transform rotate-90">Scroll</span>
            </div>
          </div>
          
          {/* Advertisement Header */}
          <PublicProtectedBlock className="bg-white p-6 md:p-10 rounded-lg shadow-md mb-8" data-aos="fade-up">
            <div className="flex flex-wrap items-center gap-4 mb-6 text-sm text-gray-600">
              <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full font-semibold">
                {advertisement.category}
              </span>
              <span className="flex items-center">
                <FaCalendarAlt className="mr-2" />
                {format(new Date(advertisement.createdAt), 'MMMM dd, yyyy')}
              </span>
              {advertisement.authorName && (
                <span className="flex items-center">
                  <FaUser className="mr-2" />
                  {advertisement.authorName}
                </span>
              )}
            </div>
            
            <h1 className="text-3xl md:text-4xl font-bold text-blue-900 mb-6" data-aos="fade-up">
              {advertisement.title}
            </h1>
            
            {advertisement.pdfUrl && (
              <div className="mb-6" data-aos="fade-up">
                <button
                  onClick={() => setShowPdfViewer(true)}
                  className="inline-flex items-center px-4 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
                >
                  <FaFilePdf className="mr-2" />
                  View PDF Document (Flipbook)
                </button>
              </div>
            )}
          </PublicProtectedBlock>
          
          {/* Advertisement Image Display */}
          {advertisement.imageUrl && (
            <PublicProtectedBlock className="mb-8 bg-gray-200 rounded-lg shadow-md flex items-center justify-center h-64 md:h-80 lg:h-96" data-aos="fade-up" data-aos-delay="200">
              <img 
                src={advertisement.imageUrl} 
                alt={advertisement.title}
                className="w-full h-full object-contain rounded-lg"
              />
            </PublicProtectedBlock>
          )}
          
          {/* Advertisement Content */}
          <PublicProtectedHtml
            className="bg-white p-6 md:p-10 rounded-lg shadow-md prose prose-lg max-w-none"
            data-aos="fade-up"
            html={advertisement.content}
          />
          
          {/* Share on Mobile */}
          <div className="mt-8 flex justify-center space-x-4 lg:hidden" data-aos="fade-up">
            <span className="text-gray-500 self-center">Share:</span>
            <button 
              onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank', 'width=600,height=400')}
              className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 transition-colors"
              title="Share on Facebook"
            >
              <FaFacebookF />
            </button>
            <button 
              onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(advertisement?.title || '')}`, '_blank', 'width=600,height=400')}
              className="w-10 h-10 rounded-full bg-blue-400 text-white flex items-center justify-center hover:bg-blue-500 transition-colors"
              title="Share on Twitter/X"
            >
              <FaTwitter />
            </button>
            <button 
              onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`, '_blank', 'width=600,height=400')}
              className="w-10 h-10 rounded-full bg-blue-700 text-white flex items-center justify-center hover:bg-blue-800 transition-colors"
              title="Share on LinkedIn"
            >
              <FaLinkedinIn />
            </button>
          </div>
        </div>
      </div>
      
      <GoToTop />
      <Footer />

      {/* PDF Flipbook Viewer Modal */}
      {showPdfViewer && advertisement.pdfUrl && (
        <PageFlipViewer
          pdfUrl={advertisement.pdfUrl}
          title={advertisement.title}
          onClose={() => setShowPdfViewer(false)}
        />
      )}
    </div>
  );
};

export default AdvertisementDetails;

